import { AccountInvestorModal } from 'components/accounts/AccountInvestorModal'
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  LabelValueList,
  PencilAltIcon,
  Typography,
  useModal,
  useRouter,
} from '@forgeglobal/design-framework'
import { aumOptions } from 'util/Enums'
import { formatAbbreviatedMoney } from 'util/NumberUtils'
import { formatEnumByLabel, formatNetWorthRange } from 'util/EnumUtils'
import { useInvestorDetailsDataQuery } from 'graphql/bindings/AdminBindings'
import React, { Fragment } from 'react'
import RightAlign from 'components/shared/RightAlign'
import capitalize from 'lodash/capitalize'

const structureLookup: { [key: string]: string } = {
  forward_backed_fund_interest: 'Forwards',
  share_backed_fund_interest: 'SPVs',
  direct_share_transfer: 'Direct',
}

const getFormattedAum = (aum: string) => formatEnumByLabel(aum, aumOptions)

const InvestorDetails: React.FC = () => {
  const { match } = useRouter<{ id: string }>()
  const { data } = useInvestorDetailsDataQuery({ variables: { id: match.params.id } })
  const account = data?.account
  const investorPrefs = account?.investor_preferences
  const accountType = account?.type
  const netWorth = account?.net_worth ?? ''
  const aum = account?.aum ?? ''
  const aumSecondaries = account?.aum_secondaries ?? ''
  const targetCheckSize = investorPrefs?.target_check_size ?? ''
  const structures = investorPrefs?.investment_structure_comfort ?? []
  const sectorsInterested = investorPrefs?.sectors_interested ?? []
  const sectorsToAvoid = investorPrefs?.sectors_avoid ?? []
  const targetRegions = investorPrefs?.target_regions ?? []
  const minValuation = investorPrefs?.minimum_company_valuation ?? undefined
  const minRevenue = investorPrefs?.minimum_company_revenue ?? undefined
  const mustBeProfitable = investorPrefs?.only_profitable_companies ?? undefined
  const companyStages = investorPrefs?.company_stages ?? []
  const notes = account?.investor_notes ?? ''

  const formattedAum = getFormattedAum(aum)

  const netWorthOrAum =
    accountType === 'individual'
      ? [{ label: 'Net worth', value: `$${formatNetWorthRange(netWorth)}`, isEmpty: !netWorth }]
      : [
          { label: 'Assets under management', value: formattedAum },
          { label: 'Assets dedicated to secondaries', value: aumSecondaries },
        ]

  const [showModal, hideModal] = useModal(() => {
    return <AccountInvestorModal hideModal={hideModal} accountId={match.params.id} />
  }, [match.params.id])

  return (
    <Card>
      <CardHeader
        title="Investor Details"
        action={
          <IconButton onClick={showModal}>
            <PencilAltIcon />
          </IconButton>
        }
      />
      <CardContent>
        <LabelValueList
          items={[
            ...netWorthOrAum,
            { label: 'Target check size', value: `$${targetCheckSize.toUpperCase()}`, isEmpty: !targetCheckSize },
            {
              label: 'Structures',
              value: (
                <RightAlign>
                  {structures.map(struct => (
                    <Chip key={struct} label={structureLookup[struct]} variant="outlined" />
                  ))}
                </RightAlign>
              ),
              isEmpty: !structures.length,
            },
            {
              label: 'Sectors of interest',
              value: (
                <RightAlign>
                  {sectorsInterested.map(sect => (
                    <Chip label={sect} key={sect} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !sectorsInterested.length,
            },
            {
              label: 'Sectors to avoid',
              value: (
                <RightAlign>
                  {sectorsToAvoid.map(sect => (
                    <Chip label={sect} key={sect} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !sectorsToAvoid.length,
            },
            {
              label: 'Target regions',
              value: (
                <RightAlign>
                  {targetRegions.map(region => (
                    <Chip label={region} variant="outlined" key={region} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !targetRegions.length,
            },
            {
              label: 'Company preferences',
              value: (
                <Fragment>
                  {minValuation && (
                    <Typography variant="body1" align="right">
                      {formatAbbreviatedMoney(minValuation)} valuation
                    </Typography>
                  )}
                  {minRevenue && (
                    <Typography variant="body1" align="right">
                      {formatAbbreviatedMoney(minRevenue)} revenue
                    </Typography>
                  )}
                  {mustBeProfitable && (
                    <Typography variant="body1" align="right">
                      Must be profitable
                    </Typography>
                  )}
                  <RightAlign>
                    {companyStages.map(stage => (
                      <Chip label={`${capitalize(stage)} stage`} variant="outlined" key={stage} />
                    ))}
                  </RightAlign>
                </Fragment>
              ),
              isEmpty: !(minValuation || minRevenue || mustBeProfitable || companyStages.length),
            },
            { label: 'Additional information', value: notes, fullWidth: !!notes, isEmpty: !notes },
          ]}
        />
      </CardContent>
    </Card>
  )
}

export { InvestorDetails }
